import { LockOutlined } from "@ant-design/icons";
import { Space, Upload, message } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import VideoIcon from "../../assets/icons/jouer.svg";
import PlusWhiteIcon from "../../assets/icons/plus-white-2.svg";
import AudioThumbnail from "../common/AudioThumbnail/AudioThumbnail";
import { Media } from "../../helpers/types";
import { StoreState } from "../../redux/store";
import { fileService } from "../../services";
import { compressImage, convertHeicFile, generateThumbnailURL, isImageAcceptedFormat } from "../../utls/FunctionsUtil";
import MailboxMediaModal from "../MailboxMediaModal";
import "./index.less";

type props = {
  showTitle?: boolean;
  crop?: boolean;
  disabled?: boolean;
  title?: string;
  maxCount?: number;
  defaultList?: any[];
  onChange?: (fileMap: Map<string, Media>, fileList?: UploadFile<any>[]) => void;
  onFilesChange?: (fileList: UploadFile<[]>[]) => void;
  children?: any;
  isVideoAllowed?: boolean;
  greenCircle?: boolean;
  addPublication?: boolean;
  iconSize?: number;
  messageId?: string;
};

const ConversationMediaComponent: React.FC<props> = ({
  showTitle = true,
  crop = false,
  disabled = false,
  title,
  maxCount,
  defaultList,
  onFilesChange,
  children,
  isVideoAllowed = true,
  greenCircle = false,
  addPublication = false,
  iconSize = 15,
  messageId,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isMediaModal, setIsMediaModal] = useState(false);
  const uploadRef = useRef<any>(null);
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const paramsFiles: Array<any[]> = JSON.parse(params.get("selected"));
  const isMedia: boolean = params.get("isMedia");
  const messageID: string = params.get("messageId");
  const [paramIds, setParamIds] = useState<any>([]);
  const { pendingMediaFiles } = useSelector((state: StoreState) => state.mediaPushes);

  const removeParams = () => {
    navigate(location.pathname, { replace: true }); // Keeps the current path but removes query params
  };

  const getAllSelectedFiles = async (fileIds: Array<any>) => {
    const selectedFileIds = fileList.map((file) => file.uid);
    const filesToFetch = fileIds.filter((id) => !selectedFileIds.includes(id));
    await fileService.allSelectedFiles(filesToFetch).then((response: any) => {
      const filesToAdd: any = [];
      response.forEach((item: any) => {
        let fileType = "";
        let fileExt = "";
        let isVideo = false;
        let isAudio = false;
        if (item.url.includes(".mp4")) {
          fileType = "video/mp4";
          fileExt = "mp4";
          isVideo = true;
        } else if (item.url.includes(".mp3")) {
          fileType = "audio/mp3";
          fileExt = "mp3";
          isAudio = true;
        } else if (item.url.includes(".wav")) {
          fileType = "audio/wav";
          fileExt = "wav";
          isAudio = true;
        } else if (item.url.includes(".m4a")) {
          fileType = "audio/m4a";
          fileExt = "m4a";
          isAudio = true;
        } else if (item.url.includes(".ogg")) {
          fileType = "audio/ogg";
          fileExt = "ogg";
          isAudio = true;
        } else if (item.url.includes(".aac")) {
          fileType = "audio/aac";
          fileExt = "aac";
          isAudio = true;
        } else if (item.url.includes(".webm")) {
          fileType = "audio/webm";
          fileExt = "webm";
          isAudio = true;
        } else {
          fileType = "image/webp";
          fileExt = "webp";
        }
        let originFileObj2 = {
          isPrivate: true,
          status: "done",
          uid: item._id,
          name: item._id,
          percent: 0,
          thumbUrl: "",
          type: "quick-link",
        };
        let JsonString = JSON.stringify(originFileObj2);
        let blob = new Blob([JsonString], { type: fileType });
        let file = new File([blob], `newQuickFile.${fileExt}`, { type: fileType });

        filesToAdd.push({
          ...item,
          imgType: "quick-file",
          isPrivate: true,
          status: "done",
          uid: item._id,
          name: item._id,
          percent: 0,
          thumbUrl: isVideo ? item?.thumbnailUrl : isAudio ? "" : item?.url,
          type: fileType,
          originFileObj: file,
        });

        //setQuickFiles((prev: any) => [...prev, { id: item._id, isPrivate: true }]);
      });

      setFileList([...pendingMediaFiles, ...filesToAdd]);
      removeParams();
    });
  };

  useEffect(() => {
    if (paramsFiles?.length !== 0 && isMedia && messageID === messageId) {
      setParamIds(paramsFiles);
    }
    return () => {
      setParamIds([]);
    };
  }, []);

  useEffect(() => {
    if (paramIds !== null && paramIds !== undefined && paramIds?.length !== 0 && isMedia && String(messageID) === String(messageId)) {
      getAllSelectedFiles(paramIds);
    }
  }, [paramIds]);

  useEffect(() => {
    if (onFilesChange !== undefined) {
      onFilesChange(fileList);
    }
  }, [fileList]);

  const customItemRender = (originNode: React.ReactNode, file: UploadFile) => {
    let lock: boolean = false;
    let isVideo: boolean = file.type?.includes("video")!;
    let isAudio: boolean = file.type?.includes("audio")!;
    defaultList?.forEach((e) => {
      if (file.uid === e.uid && e.isPrivate) {
        lock = true;
      }
    });
    return (
      <>
        {isAudio ? <AudioThumbnail /> : originNode}
        {lock && <LockOutlined className="lockIcon" />}
        {isVideo && <img src={VideoIcon} alt="video icon" className={`videoIcon ${addPublication ? "postVideoIcon" : ""}`} />}
      </>
    );
  };

  const beforeUpload: UploadProps["beforeUpload"] = async (file: RcFile): Promise<File | boolean | string> => {
    try {
      //console.log("in before **upload** file -", file.uid, file);
      // If the file is in an accepted format, proceed without conversion
      if (isImageAcceptedFormat(file.type)) {
        // compress file
        const compressedImage = await compressImage(file);
        //console.log("beforeUpload 1 compressedImage", compressedImage);
        if (compressedImage !== null) {
          handleConvertedFile(file.uid, compressedImage, true);
        }
        //handleConvertedFile(file.uid, file, true);
        return false;
      } else if (file.type === "image/heic" || file.type === "") {
        handleConvertedFile(file.uid, file, false);
        // convert file
        const convertedFile = await convertHeicFile(file);
        //console.log("beforeUpload 2 convertedFile", convertedFile);
        // compress file
        const compressedImage = await compressImage(convertedFile);
        //console.log("beforeUpload 3 compressedImage", compressedImage);
        if (compressedImage !== null) {
          handleConvertedFile(file.uid, compressedImage, true);
        }
        return false;
      } else if (file.type.includes("video/") && isVideoAllowed) {
        handleConvertedFile(file.uid, file, true);
        return false;
      } else if (file.type.includes("audio/")) {
        handleConvertedFile(file.uid, file, true);
        return false;
      } else {
        message.error(t("unsupported-file-format-error-message"));
        return Upload.LIST_IGNORE;
      }
    } catch (error) {
      removeUnsupportedFiles(file.uid);
      message.error(t("unsupported-file-format-error-message"));
      return Upload.LIST_IGNORE;
    }
  };

  const handleConvertedFile = async (uid: string, file: File, converted: boolean) => {
    //console.log("uid, converted, file :>> ", uid, converted, file);
    if (!converted) {
      const fileObj: UploadFile = {
        uid: uid,
        name: file.name,
        status: "uploading",
        percent: 50,
      };
      setFileList((prevList) => [fileObj, ...prevList]);
    } else {
      const fileObj: UploadFile = {
        uid: uid,
        name: file.name,
        status: "done",
        type: file.type,
        size: file.size,
        originFileObj: file as RcFile,
      };
      if (file.type.includes("video")) {
        const result = await generateThumbnailURL(file as RcFile);
        fileObj.url = result;
      } else if (file.type.includes("audio")) {
        fileObj.url = "";
      }
      setFileList((prevList) => [fileObj, ...prevList.filter((file) => file.uid !== fileObj.uid)]);
    }
  };

  const isImageUrl = (file: UploadFile): boolean => {
    // Do not render as image for audio, we render a custom AudioThumbnail
    if (file.type?.includes("audio/")) return false;
    return true;
  };

  const removeUnsupportedFiles = (uid: string) => {
    setFileList((prevList) => [...prevList.filter((file) => file.uid !== uid)]);
  };

  const onRemove = async (file: UploadFile<any>) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  // const onPreview = async (file: UploadFile) => {
  //   let src = file.url as string;
  //   if (!src) {
  //     src = await new Promise((resolve) => {
  //       const reader = new FileReader();
  //       reader.readAsDataURL(file.originFileObj as RcFile);
  //       reader.onload = () => resolve(reader.result as string);
  //     });
  //   }
  //   const image = new Image();
  //   image.src = src;
  //   const imgWindow = window.open(src);
  //   imgWindow?.document.write(image.outerHTML);
  // };

  const handleDeviceClick = () => {
    if (uploadRef.current) {
      uploadRef.current.click();
      setIsMediaModal(false);
    }
  };

  return (
    <Space className={`gap-13 coversationMediaWrapper `} direction="vertical">
      {fileList.length === 0 ? (
        <div
          className="d-flex justify-content-center"
          onClick={() => {
            if (!disabled) {
              setIsMediaModal(true);
            }
          }}
        >
          {maxCount ? (
            fileList.length < maxCount && (
              <div className={`addCircleGreen ${disabled ? "disabled" : ""}`}>
                <img src={PlusWhiteIcon} width={70} alt="plus" />
              </div>
            )
          ) : (
            <div className={`addCircleGreen ${disabled ? "disabled" : ""}`}>
              <img src={PlusWhiteIcon} width={70} alt="plus" />
            </div>
          )}
        </div>
      ) : (
        <div
          className="rectangularShape"
          onClick={() => {
            if (!disabled) {
              setIsMediaModal(true);
            }
          }}
        >
          {maxCount ? (
            fileList.length < maxCount && (
              <div className={`addCircle ${disabled ? "disabled" : ""}`}>
                <img src={PlusWhiteIcon} width={70} alt="plus" />
              </div>
            )
          ) : (
            <div className={`addCircle ${disabled ? "disabled" : ""}`}>
              <img src={PlusWhiteIcon} width={70} alt="plus" />
            </div>
          )}
        </div>
      )}

      <MailboxMediaModal
        isOpen={isMediaModal}
        onClose={() => setIsMediaModal(false)}
        onMyAlbums={() => {
          navigate(`/creator/quickLink?id=pendingMedia&conversationId=${conversationId}&messageId=${messageId}`);
        }}
        onMyDevice={handleDeviceClick}
      />

      <Upload
        accept="image/*,video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm"
        disabled={disabled}
        action=""
        itemRender={(originNode: any, file: any) => customItemRender(originNode, file)}
        defaultFileList={defaultList}
        fileList={fileList}
        listType="picture-card"
        maxCount={maxCount}
        onRemove={onRemove}
        beforeUpload={beforeUpload}
        isImageUrl={isImageUrl}
        showUploadList={{
          showRemoveIcon: true,
          showPreviewIcon: false,
        }}
        multiple={true}
      >
        {/* {children !== undefined ? (
            children
          ) : maxCount ? (
            fileList.length < maxCount && (
              <div className={`addCircle ${disabled ? "disabled" : ""}`}>
                <img src={PlusWhiteIcon} width={25} alt="plus" />
              </div>
            )
          ) : (
            <div className={`addCircle ${disabled ? "disabled" : ""}`}>
              <img src={PlusWhiteIcon} width={25} alt="plus" />
            </div>
          )} */}
        <div ref={uploadRef}></div>
      </Upload>
    </Space>
  );
};

export default ConversationMediaComponent;
